import React, { useState, useRef, useEffect } from 'react';
import Tooltip from '../ui/Tooltip';
import LayoutPresetsMenu from '../ui/LayoutPresetsMenu';
import AvatarButton, { AVATAR_BUTTON_DIAMETERS } from '../ui/AvatarButton';
import { getBranding } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import { getAppLogoSrc } from '../../constants/branding';
import { getAccountDisplayName } from '../../lib/accountDisplay';
import { resolveSidebarExpandedLogo, resolveThemeAwarePrimaryLogo, type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';
import { getTextLogoInitials, getTextLogoName } from '../../lib/textLogo';
import { ChartNoAxesCombined } from '../../constants';
import {
  LayoutDashboard,
  CalendarDays,
  FileText,
  CandlestickChart,
  NotebookPen,
  Search,
  Plus,
  Menu,
  X,
  CheckCircle2,
  ShieldPlus,
  UploadCloud as CloudUpload,
  Settings,
  LifeBuoy,
  LogOut,
  Flag,
  Briefcase,
  Moon,
  Sun,
} from 'lucide-react';
import { signOut } from '../../lib/auth.ts';

// --- Constants ---
// --- Helper Components ---

/**
 * PanelsLeftFilled - Custom icon with a filled left (sidebar) section.
 * Self-contained SVG component.
 */
const PanelsLeftFilled: React.FC<{ className?: string }> = ({ className = 'w-12 h-12' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4V3Z" fill="currentColor" stroke="none" />
    <path d="M3 9h18" />
  </svg>
);

const UserActions: React.FC<{
  onNavigate: (p: string) => void;
  onLogoutStart?: () => void;
  displayName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  setNavLayout?: (v: 'sidebar' | 'top') => void;
  navLayout?: 'sidebar' | 'top';
  theme?: 'light' | 'dark';
  setTheme?: (t: 'light' | 'dark') => void;
  canSwitchTheme?: boolean;
  activePage?: string;
  layoutPresetsButtonRef?: React.RefObject<HTMLButtonElement>;
  showAdminTools?: boolean;
  /** Show the Business (Partner Portal) item; server-authorized (platform admin OR tenant business admin). */
  showBusiness?: boolean;
  tenantSlug?: string | null;
}> = ({ onNavigate, onLogoutStart, displayName, email, avatarUrl, setNavLayout, navLayout, theme = 'dark', setTheme, canSwitchTheme = true, activePage, layoutPresetsButtonRef, showAdminTools = false, showBusiness = false, tenantSlug }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const searchRef = useRef<HTMLButtonElement | null>(null);
  const [searchHover, setSearchHover] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement | null>(null);
  const [navToggleHover, setNavToggleHover] = useState(false);
  const [confirmationToast, setConfirmationToast] = useState<string | null>(null);
  const [confirmationToastFading, setConfirmationToastFading] = useState(false);
  const resolvedDisplayName = getAccountDisplayName(displayName, email);
  const confirmationToastTimer = useRef<number | null>(null);
  const confirmationToastFadeTimer = useRef<number | null>(null);
  // Local theme helpers (was referenced but defined in parent)
  const isDark = theme === 'dark';
  const hoverBgClass = isDark ? 'hover:bg-[#0e204a]' : 'hover:bg-blue-800';

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!(e.target instanceof Node)) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Listen to open/close user actions menu events for onboarding tour integration
  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    window.addEventListener('dashboard:open-user-menu', onOpen);
    window.addEventListener('dashboard:close-user-menu', onClose);
    return () => {
      window.removeEventListener('dashboard:open-user-menu', onOpen);
      window.removeEventListener('dashboard:close-user-menu', onClose);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (confirmationToastTimer.current) window.clearTimeout(confirmationToastTimer.current);
      if (confirmationToastFadeTimer.current) window.clearTimeout(confirmationToastFadeTimer.current);
    };
  }, []);

  useEffect(() => {
    const onToolbarConfirmation = (event: Event) => {
      try {
        const detail = (event as CustomEvent<{ message?: string }>).detail;
        const message = typeof detail?.message === 'string' ? detail.message.trim() : '';
        if (!message) return;

        setConfirmationToast(message);
        setConfirmationToastFading(false);
        if (confirmationToastFadeTimer.current) window.clearTimeout(confirmationToastFadeTimer.current);
        if (confirmationToastTimer.current) window.clearTimeout(confirmationToastTimer.current);
        confirmationToastFadeTimer.current = window.setTimeout(() => setConfirmationToastFading(true), 1200) as unknown as number;
        confirmationToastTimer.current = window.setTimeout(() => {
          setConfirmationToast(null);
          setConfirmationToastFading(false);
        }, 1700) as unknown as number;
      } catch {}
    };

    window.addEventListener('dashboard:toolbar-confirmation', onToolbarConfirmation as EventListener);
    return () => window.removeEventListener('dashboard:toolbar-confirmation', onToolbarConfirmation as EventListener);
  }, []);

  const handleLogout = async () => {
    try {
      onLogoutStart?.();
      await signOut();
    } catch {
      // ignore
    }
  };

  const tooltipClass = theme === 'dark' ? 'bg-blue-600 text-white border-blue-700' : '';
  const accountMenuName = resolvedDisplayName || email || 'Account';
  const menuButtonBaseClass = 'w-full flex items-center px-5 py-3 text-[15px] outline-none transition-colors';
  const menuSurfaceClass = isDark
    ? 'border border-[#222634] bg-[#13151c] shadow-2xl'
    : 'border border-zinc-200 bg-white shadow-[0_24px_50px_rgba(15,23,42,0.12)]';
  const menuHeaderNameClass = isDark
    ? '-mb-[5px] text-center text-[25px] font-medium italic leading-none tracking-normal text-gray-200'
    : '-mb-[5px] text-center text-[25px] font-medium italic leading-none tracking-normal text-zinc-900';
  const menuDividerClass = isDark ? 'border-[#222634]' : 'border-zinc-200';
  const menuHeaderSurfaceClass = isDark ? 'bg-[#091431]' : 'bg-zinc-100';
  const menuPrimaryItemClass = 'bg-blue-600 text-white hover:bg-blue-500 focus:bg-blue-500';
  const menuNeutralItemClass = isDark
    ? 'text-gray-200 hover:bg-[#1c1e26] hover:text-white focus:bg-[#1c1e26] focus:text-white'
    : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950 focus:bg-zinc-100 focus:text-zinc-950';
  const menuItemClass = (page: string) =>
    `${menuButtonBaseClass} ${activePage === page ? menuPrimaryItemClass : menuNeutralItemClass}`;
  const menuItemIconClass = (page: string) =>
    `mr-3 h-[18px] w-[18px] shrink-0 ${activePage === page ? 'text-white' : menuNeutralIconClass}`;
  const menuLogoutItemClass = isDark
    ? 'text-red-400 hover:bg-[#1c1e26] hover:text-red-300 focus:bg-[#1c1e26] focus:text-red-300'
    : 'text-rose-600 hover:bg-rose-50 hover:text-rose-700 focus:bg-rose-50 focus:text-rose-700';
  const menuNeutralIconClass = isDark ? 'text-gray-300' : 'text-zinc-500';
  const menuLogoutIconClass = isDark ? 'text-red-400' : 'text-rose-500';
  const menuThemeIconClass = isDark ? 'text-amber-300' : 'text-amber-500';
  const themeActionLabel = isDark ? 'Light mode' : 'Dark mode';
  const confirmationToastSurfaceClass = isDark
    ? 'border border-[#7A3E00] bg-[#2B1406] shadow-[0_10px_22px_-10px_rgba(0,0,0,0.6)]'
    : 'border border-zinc-200 bg-white shadow-[0_10px_22px_-10px_rgba(15,23,42,0.22)]';
  const confirmationToastIconClass = isDark ? 'text-[#FFD7A3]' : 'text-emerald-600';
  const confirmationToastTextClass = isDark ? 'text-[#FFE8CF]' : 'text-zinc-800';

  const closeAndNavigate = (page: string) => {
    setOpen(false);
    onNavigate(page);
  };

  return (
    <div className="relative flex items-center gap-3" ref={ref}>
      <div className="relative flex items-center">
        {confirmationToast && (
          <div
            className={`absolute right-full mr-2 top-1/2 -translate-y-1/2 z-50 ${confirmationToastFading ? 'opacity-0 -translate-x-2' : 'opacity-100 translate-x-0'}`}
            style={{ transition: 'opacity 300ms, transform 300ms' }}
          >
            <div className={`flex items-center gap-3 rounded-md px-4 py-2 whitespace-nowrap ${confirmationToastSurfaceClass}`}>
              <CheckCircle2 className={`h-7 w-7 ${confirmationToastIconClass}`} />
              <span className={`text-sm leading-none tracking-tight ${confirmationToastTextClass}`}>
                {confirmationToast}
              </span>
            </div>
          </div>
        )}
        <button
          ref={searchRef}
          aria-label="Search"
          onClick={() => onNavigate('Search')}
          onMouseEnter={() => setSearchHover(true)}
          onMouseLeave={() => setSearchHover(false)}
          onFocus={() => setSearchHover(true)}
          onBlur={() => setSearchHover(false)}
          className={`${isDark ? 'text-slate-400' : 'text-zinc-700'} hover:text-white p-2 rounded-lg ${hoverBgClass} transition-colors`}
        >
          <Search className="w-5 h-5" />
        </button>
        {searchRef.current && (
          <Tooltip anchorRef={searchRef} open={searchHover} side="top" offset={8} customClassName={tooltipClass}>
            Search
          </Tooltip>
        )}
      </div>

      {activePage === 'Dashboard' && (
        <div className="relative flex items-center">
          <LayoutPresetsMenu menuButtonRef={layoutPresetsButtonRef} tenantSlug={tenantSlug} />
        </div>
      )}

      {setNavLayout && (
        <div className="relative flex items-center ml-2">
          <button
            type="button"
            ref={navToggleRef}
            aria-label="Toggle navigation layout"
            data-tour-target="navigation-layout-toggle"
            onClick={() => setNavLayout('sidebar')}
            onMouseEnter={() => setNavToggleHover(true)}
            onMouseLeave={() => setNavToggleHover(false)}
            onFocus={() => setNavToggleHover(true)}
            onBlur={() => setNavToggleHover(false)}
            className={`group text-slate-400 ${!isDark ? 'hover:text-white' : ''} p-2 rounded-lg ${hoverBgClass} transition-colors`}
          >
            <PanelsLeftFilled className={`w-6 h-6 group-hover:text-white ${isDark ? '' : 'text-zinc-600'}`} />
          </button>
          <Tooltip anchorRef={navToggleRef} open={navToggleHover} side="bottom" offset={6} customClassName={tooltipClass}>
            Side menu
          </Tooltip>
        </div>
      )}

      {/* Right-side New Trade button removed per request */}

      <div className="flex items-center gap-2 pl-2">
        {resolvedDisplayName && !open && navLayout !== 'top' ? (
          <div className="hidden xl:flex flex-col items-end leading-tight pr-1">
            <span className={`text-[11px] font-semibold ${isDark ? 'text-slate-100' : 'text-zinc-800'}`}>{resolvedDisplayName}</span>
            <span className={`text-[10px] uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-zinc-500'}`}>Account</span>
          </div>
        ) : null}
        <div className="relative h-14 w-[76px]">
          <div className="absolute right-0 top-6 z-[60] translate-x-2 -translate-y-[38%] overflow-visible">
            <AvatarButton
              name={resolvedDisplayName}
              email={email}
              avatarUrl={avatarUrl}
              theme={theme}
              onClick={() => setOpen(o => !o)}
              showChevron
              chevronDirection={open ? 'up' : 'down'}
              diameter={AVATAR_BUTTON_DIAMETERS.topMenu}
              ariaLabel="Account menu"
              data-tour-target="user-menu-button"
            />
          </div>
        </div>
      </div>

      {setNavLayout && (
        null
      )}

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className={`absolute right-0 top-full z-50 mt-0 w-[280px] overflow-hidden rounded-2xl ${menuSurfaceClass}`}
        >
          <div className={`border-b px-5 py-4 ${menuDividerClass} ${menuHeaderSurfaceClass}`}>
            <div
              className={menuHeaderNameClass}
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              {accountMenuName}
            </div>
          </div>

          <div className="flex flex-col">
            <button
              role="menuitem"
              onClick={() => closeAndNavigate('Import Trades')}
              aria-current={activePage === 'Import Trades' ? 'page' : undefined}
              data-tour-target="user-menu-import-trades"
              className={menuItemClass('Import Trades')}
            >
              <CloudUpload className="mr-3 h-[18px] w-[18px] shrink-0" strokeWidth={2.5} />
              <span className="font-semibold text-[15px]">Import Trades</span>
            </button>
            <button
              role="menuitem"
              onClick={() => closeAndNavigate('Settings')}
              aria-current={activePage === 'Settings' ? 'page' : undefined}
              data-tour-target="user-menu-settings"
              className={menuItemClass('Settings')}
            >
              <Settings className={menuItemIconClass('Settings')} strokeWidth={2} />
              <span className="font-medium text-[15px]">Settings</span>
            </button>
            {currentRuntime.brand.id === 'warrior-trading' ? (
              <button
                role="menuitem"
                onClick={() => closeAndNavigate('Challenges')}
                aria-current={activePage === 'Challenges' ? 'page' : undefined}
                data-tour-target="user-menu-challenges"
                className={menuItemClass('Challenges')}
              >
                <Flag className={menuItemIconClass('Challenges')} strokeWidth={2} />
                <span className="font-medium text-[15px]">Challenges</span>
              </button>
            ) : null}
            {showBusiness ? (
              <button
                role="menuitem"
                onClick={() => closeAndNavigate('Business')}
                aria-current={activePage === 'Business' ? 'page' : undefined}
                className={menuItemClass('Business')}
              >
                <Briefcase className={menuItemIconClass('Business')} strokeWidth={2} />
                <span className="font-medium text-[15px]">Business</span>
              </button>
            ) : null}
            {showAdminTools ? (
              <button
                role="menuitem"
                onClick={() => closeAndNavigate('Admin Tools')}
                aria-current={activePage === 'Admin Tools' ? 'page' : undefined}
                className={menuItemClass('Admin Tools')}
              >
                <ShieldPlus className={menuItemIconClass('Admin Tools')} strokeWidth={2} />
                <span className="font-medium text-[15px]">Admin Tools</span>
              </button>
            ) : null}
            <button
              role="menuitem"
              onClick={() => closeAndNavigate('Support')}
              aria-current={activePage === 'Support' ? 'page' : undefined}
              data-tour-target="user-menu-support"
              className={menuItemClass('Support')}
            >
              <LifeBuoy className={menuItemIconClass('Support')} strokeWidth={2} />
              <span className="font-medium text-[15px]">Support</span>
            </button>
            {canSwitchTheme && setTheme ? (<button
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                setTheme?.(isDark ? 'light' : 'dark');
              }}
              className={`${menuButtonBaseClass} ${menuNeutralItemClass}`}
            >
              {isDark ? (
                <Sun className={`mr-3 h-[18px] w-[18px] shrink-0 ${menuThemeIconClass}`} strokeWidth={2} />
              ) : (
                <Moon className="mr-3 h-[18px] w-[18px] shrink-0 text-slate-500" strokeWidth={2} />
              )}
              <span className="font-medium text-[15px]">{themeActionLabel}</span>
            </button>) : null}
          </div>

          <div className={`border-t ${menuDividerClass}`} />

          <button
            role="menuitem"
            onClick={handleLogout}
            className={`${menuButtonBaseClass} ${menuLogoutItemClass}`}
          >
            <LogOut className={`mr-3 h-[18px] w-[18px] shrink-0 ${menuLogoutIconClass}`} strokeWidth={2} />
            <span className="font-medium text-[15px]">Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

// --- Main Glass Navbar Component ---

type Props = {
  active?: string;
  onNavigate?: (p: string) => void;
  onLogoutStart?: () => void;
  avatarUrl?: string | null;
  displayName?: string | null;
  email?: string | null;
  navLayout?: 'sidebar' | 'top';
  setNavLayout?: (v: 'sidebar' | 'top') => void;
  theme?: 'light' | 'dark';
  setTheme?: (t: 'light' | 'dark') => void;
  canSwitchTheme?: boolean;
  layoutPresetsButtonRef?: React.RefObject<HTMLButtonElement>;
  showAdminTools?: boolean;
  showBusiness?: boolean;
  containerSpacingClassName?: string;
  hasBottomBorder?: boolean;
  locationLabel?: string | null;
  whiteLabel?: WhiteLabelAppBranding | null;
};

const GlassNavbar: React.FC<Props> = ({ active, onNavigate, onLogoutStart, avatarUrl, displayName, email, navLayout, setNavLayout, theme = 'dark', setTheme, canSwitchTheme = true, layoutPresetsButtonRef, showAdminTools = false, showBusiness = false, containerSpacingClassName = 'mb-0', hasBottomBorder = true, whiteLabel = null }) => {
  const [internalActiveTab, setInternalActiveTab] = useState('Dashboard');
  const activeTab = active || internalActiveTab;
  const navigate = onNavigate || ((p:string)=> setInternalActiveTab(p));
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileToggleRef = useRef<HTMLButtonElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!mobileOpen) return;

    const closeWhenOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (mobileToggleRef.current?.contains(event.target) || mobileMenuRef.current?.contains(event.target)) return;
      setMobileOpen(false);
    };

    document.addEventListener('pointerdown', closeWhenOutside);
    return () => document.removeEventListener('pointerdown', closeWhenOutside);
  }, [mobileOpen]);

  const navItems = [
    { name: 'Dashboard', icon: LayoutDashboard },
    { name: 'Calendar', icon: CalendarDays },
    { name: 'Reports', icon: ChartNoAxesCombined },
    { name: 'Trades', icon: CandlestickChart },
    { name: 'Journal', icon: NotebookPen },
  ];

  const leftItems = navItems.slice(0, 5);

  const isDark = theme === 'dark';
  const isPjTradesTenant = whiteLabel?.tenantSlug === 'pjtrades';
  const isStockDadsTenant = whiteLabel?.tenantSlug === 'stockdads';
  const isJDubTradesTenant = whiteLabel?.tenantSlug === 'jdub';
  const isTravelingTraderTenant = whiteLabel?.tenantSlug === 'traveling-trader';
  const isHerTenant = whiteLabel?.tenantSlug === 'her';
  const isChartGuysTenant = whiteLabel?.tenantSlug === 'chartguys';
  const isPbAlertsTenant = whiteLabel?.tenantSlug === 'pbalerts';
  const isGemFuturesTenant = whiteLabel?.tenantSlug === 'gem-futures';
  const containerClass = isDark
    ? 'bg-slate-950 text-slate-200 border-b border-slate-900'
    : `${isPjTradesTenant ? 'bg-[#e0dfdf]' : 'bg-sky-100'} text-zinc-900 border-b ${isPjTradesTenant ? 'border-[#e0dfdf]' : 'border-sky-100'}`;
  const strokeGradient = isDark ? 'from-transparent via-white/10 to-transparent' : 'from-transparent via-blue-400/40 to-transparent';
  const glowBackground = isDark
    ? 'linear-gradient(180deg, rgba(59,130,246,0) 0%, rgba(59,130,246,0.06) 28%, rgba(59,130,246,0.16) 100%)'
    : 'linear-gradient(180deg, rgba(59,130,246,0) 0%, rgba(59,130,246,0.03) 28%, rgba(59,130,246,0.06) 100%)';
  const brand = getBranding(currentRuntime.brand.id);
  const horizontalLogoSrc = resolveThemeAwarePrimaryLogo(whiteLabel, isDark ? 'dark' : 'light')
    || getAppLogoSrc({ variant: 'horizontalSplit', theme: isDark ? 'dark' : 'light' });
  const isFirstPartyTradeProof = currentRuntime.brand.id === 'trade-proof'
    && (!whiteLabel?.tenantSlug || whiteLabel.tenantSlug === 'trade-proof');
  const isBearBullTenant = whiteLabel?.tenantSlug === 'bearbull';
  const isBullishBearsTenant = whiteLabel?.tenantSlug === 'bullish-bears';
  const isMicTenant = whiteLabel?.tenantSlug === 'mic';
  const isStockNavigatorsTenant = whiteLabel?.tenantSlug === 'stock-navigators';
  const isWarriorTradingTenant = whiteLabel?.tenantSlug === 'warrior-trading';
  const isBowsTenant = whiteLabel?.tenantSlug === 'bows';
  const isDemonTradingTenant = whiteLabel?.tenantSlug === 'demontrading';
  const isTradingWithRustyRodTenant = whiteLabel?.tenantSlug === 'trading-with-rustyrod';
  const isHumbledTraderTenant = whiteLabel?.tenantSlug === 'humbled-trader';
  const isPropHubTradingTenant = whiteLabel?.tenantSlug === 'prophub-trading';
  const isNquartersTenant = whiteLabel?.tenantSlug === 'nquarters';
  const isTradingTheTrendTenant = whiteLabel?.tenantSlug === 'trading-the-trend';
  const isStockTradingProTenant = whiteLabel?.tenantSlug === 'stock-trading-pro';
  const isTheNoirExchange = whiteLabel?.tenantSlug === 'the-noir-exchange';
  const isPbTradingTenant = whiteLabel?.tenantSlug === 'pb-trading';
  const isTeamBullTradingTenant = whiteLabel?.tenantSlug === 'team-bull-trading';
  const isLantoTradesTenant = whiteLabel?.tenantSlug === 'lanto-trades';
  const isGoldPipsTenant = whiteLabel?.tenantSlug === 'gold-pips';
  const isStockTalkInsidersTenant = whiteLabel?.tenantSlug === 'stock-talk-insiders';
  const isTheHavenTenant = whiteLabel?.tenantSlug === 'the-haven';
  const isElevateTenant = whiteLabel?.tenantSlug === 'elevate';
  const isMarketPulseTenant = whiteLabel?.tenantSlug === 'marketpulse';
  const isInTheCircleTenant = whiteLabel?.tenantSlug === 'inthecircle';
  const isToriTradesTenant = whiteLabel?.tenantSlug === 'tori-trades';
  const compactTenantLogoSrc = isFirstPartyTradeProof
    ? getAppLogoSrc({ variant: 'stacked', theme: isDark ? 'dark' : 'light' })
    : isWarriorTradingTenant
        ? getAppLogoSrc({ variant: 'horizontalLockup', theme: isDark ? 'dark' : 'light' })
      : isBearBullTenant
        ? horizontalLogoSrc
      : resolveSidebarExpandedLogo(whiteLabel, isDark ? 'dark' : 'light') || horizontalLogoSrc;
  const topNavLogoScale = brand.logos.topNavLogoScale ?? 1;
  const topNavRenderedLogoScale = topNavLogoScale * (isDemonTradingTenant ? 0.7 : isMicTenant ? 0.8 : isStockNavigatorsTenant ? 0.85 : isPjTradesTenant ? 0.55 : isStockDadsTenant ? 0.8 : isJDubTradesTenant ? 0.8 : isTravelingTraderTenant ? 0.8 : isHerTenant ? 0.9 : isChartGuysTenant ? 0.8 : isPbAlertsTenant ? 0.8 : isBearBullTenant ? 1.45 : isTradingWithRustyRodTenant ? 0.8 : isGemFuturesTenant ? 0.9 : isHumbledTraderTenant ? 0.75 : isPropHubTradingTenant ? 0.55 : isNquartersTenant ? 0.7 : isTradingTheTrendTenant ? 0.65 : isStockTradingProTenant ? 0.7 : isTheNoirExchange ? 0.7 : isPbTradingTenant ? 0.55 : isTeamBullTradingTenant ? 0.9 : isLantoTradesTenant ? 0.6 : isGoldPipsTenant ? 0.9 : isStockTalkInsidersTenant ? 0.85 : isTheHavenTenant ? 0.58 : isElevateTenant ? 0.7 : isMarketPulseTenant ? 0.9 : isInTheCircleTenant ? 0.75 : isToriTradesTenant ? 0.7 : 1);
  // The logo halo/glow is platform branding only: keep it for the base Trade-Proof
  // and Warrior Trading tenants, and turn it off for every white-label tenant
  // (current and future), derived from the resolved tenant slug.

  // Use a pseudo-element hover background so visual highlight does not affect layout/spacing.
  const menuButtonBaseClass =
    "relative isolate flex flex-col items-center gap-1.5 group transition-colors rounded-md before:content-[''] before:absolute before:inset-[-8px_-12px] before:rounded-md before:opacity-0 before:transition-opacity before:duration-150 before:ease-out before:z-0 after:content-[''] after:absolute after:inset-[-12px_-16px] after:rounded-md after:opacity-0 after:transition-opacity after:duration-150 after:ease-out after:blur-xl after:-z-10";
  const menuButtonInactiveClass = isDark
    ? 'text-white hover:text-blue-300 hover:before:opacity-100 before:bg-gradient-to-b before:from-blue-300/25 before:to-blue-950/5'
    : 'text-zinc-700 hover:text-blue-600 hover:before:opacity-100 before:bg-gradient-to-b before:from-blue-500/30 before:to-blue-400/8 after:bg-blue-500/25 hover:after:opacity-80';
  const menuButtonActiveClass = isDark
    ? "text-blue-600 before:opacity-100 before:bg-gradient-to-b before:from-blue-700/50 before:to-blue-950/30 after:content-[''] after:absolute after:inset-[-12px_-16px] after:rounded-md after:bg-blue-700/35 after:blur-xl after:opacity-95 after:-z-10"
    : "text-blue-600 before:opacity-100 before:bg-gradient-to-b before:from-blue-500/30 before:to-blue-400/8 after:content-[''] after:absolute after:inset-[-12px_-16px] after:rounded-md after:bg-blue-500/25 after:blur-xl after:opacity-80 after:-z-10";
  const menuIconInactiveClass = isDark
    ? 'text-white group-hover:text-blue-300'
    : 'text-zinc-700 group-hover:text-blue-600';
  const activeTextClass = isDark ? 'text-white' : 'text-blue-600';
  const activeIconClass = activeTextClass;
  const bottomBorderClassName = hasBottomBorder
    ? (isDark ? 'border-b border-slate-900' : 'border-b border-sky-100')
    : 'border-b-0';

  return (
    <div className={`sticky top-0 z-[1002] overflow-visible ${containerClass} ${bottomBorderClassName} w-full ${containerSpacingClassName}`}> {/* Container wrapper for background context */}

      <nav
        className="relative z-[1003] grid h-14 w-full grid-cols-[minmax(0,1fr)_minmax(0,28rem)_minmax(0,1fr)] items-center gap-4 overflow-visible px-4 font-sans md:h-[5.5rem] md:px-6"
        data-tour-target="top-navigation"
      >
        {/* Compact navigation appears before the full row can collide with the protected logo zone. */}
        <div className="relative flex min-w-0 items-center justify-self-start min-[1440px]:hidden">
          <button
            ref={mobileToggleRef}
            onClick={() => setMobileOpen(o => !o)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? 'Close primary navigation' : 'Open primary navigation'}
            className="inline-flex h-9 w-9 min-h-0 min-w-0 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-slate-200 dark:hover:bg-[#0e204a]"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          {(compactTenantLogoSrc || whiteLabel?.useTextLogo) && (
            <div className="flex h-[5.5rem] min-h-0 min-w-0 items-center">
            {whiteLabel?.useTextLogo ? (
              <span
                className="ml-4 max-w-[67.2vw] truncate text-center text-lg font-semibold tracking-wide text-slate-900 dark:text-white min-[1440px]:hidden"
                aria-label={whiteLabel.companyName}
              >
                {getTextLogoInitials(whiteLabel.companyName)}
              </span>
            ) : <img
                src={compactTenantLogoSrc}
                alt={whiteLabel?.appName || brand.appName || 'Trade-Proof'}
                style={isBullishBearsTenant ? { height: '5rem', width: '14rem', maxHeight: '4.8rem', transform: 'translateX(0)' } : isBearBullTenant ? { transform: 'translateX(30px) scale(1.2)' } : undefined}
                className={`${isWarriorTradingTenant ? 'ml-2' : isMicTenant ? 'ml-2' : isBullishBearsTenant ? '-ml-1' : 'ml-4'} max-h-full object-contain min-[1440px]:hidden ${isFirstPartyTradeProof
                ? 'h-[4.5rem] w-[12.6rem] max-w-[50.4vw]'
                : isBullishBearsTenant
                  ? 'h-[4.25rem] w-[12rem] max-w-[48vw]'
                : isMicTenant
                  ? 'h-[4.5rem] w-[12.6rem] max-w-[50.4vw]'
                : isWarriorTradingTenant
                  ? 'h-[5rem] w-[16rem] max-w-[56vw]'
                : 'h-[6rem] w-[16.8rem] max-w-[67.2vw]'}`}
            />}
            </div>
          )}
          {mobileOpen && (
            <div
              ref={mobileMenuRef}
              data-testid="compact-primary-navigation"
              className="absolute left-0 top-full z-50 mt-1 w-[min(20rem,calc(100vw-2rem))]"
            >
              <div className="animate-in slide-in-from-top-2 duration-200 max-h-[calc(100dvh-7rem)] overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-xl overscroll-contain dark:border-[#222634] dark:bg-[#13151c] dark:shadow-2xl">
                <div className="flex flex-col divide-y divide-zinc-200 dark:divide-[#222634]">
                  {navItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => { setMobileOpen(false); navigate(item.name); }}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === item.name
                        ? 'bg-blue-600 text-white hover:bg-blue-500 focus:bg-blue-500'
                        : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-950 focus:bg-zinc-50 focus:text-zinc-950 dark:text-gray-200 dark:hover:bg-[#1c1e26] dark:hover:text-white dark:focus:bg-[#1c1e26] dark:focus:text-white'}`}
                    >
                      <item.icon className={`h-5 w-5 ${activeTab === item.name ? 'text-white' : 'text-zinc-700 dark:text-gray-300'}`} />
                      <span className="font-medium">{item.name === 'Reports' ? 'Trends' : item.name}</span>
                    </button>
                  ))}

                  <button
                    onClick={() => { setMobileOpen(false); navigate('New Trade'); }}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === 'New Trade'
                      ? 'bg-blue-600 text-white hover:bg-blue-500 focus:bg-blue-500'
                      : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-950 focus:bg-zinc-50 focus:text-zinc-950 dark:text-gray-200 dark:hover:bg-[#1c1e26] dark:hover:text-white dark:focus:bg-[#1c1e26] dark:focus:text-white'}`}
                  >
                    <FileText className={`h-5 w-5 ${activeTab === 'New Trade' ? 'text-white' : 'text-zinc-700 dark:text-gray-300'}`} />
                    <span className="font-medium">New Trade</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Background Horizontal Line removed */}

        {/* Protected center zone: no navigation item is allowed beneath the logo. */}
          <div
            data-testid="primary-navigation-logo"
            className={`relative col-start-2 row-start-1 z-[1014] hidden min-w-0 ${isBowsTenant ? 'translate-y-[10px]' : whiteLabel?.tenantSlug === 'mic' ? 'translate-y-[0px]' : whiteLabel?.tenantSlug === 'fullport-university' ? 'translate-y-[10px]' : isTradingWithRustyRodTenant ? 'translate-y-[2px]' : isTradingTheTrendTenant ? 'translate-y-[2px]' : isTeamBullTradingTenant ? 'translate-y-[2px]' : isLantoTradesTenant ? 'translate-y-[1px]' : isTheHavenTenant ? 'translate-y-[2px]' : isToriTradesTenant ? 'translate-y-[0px]' : 'translate-y-[5px]'} justify-center overflow-visible pointer-events-none min-[1440px]:flex`}
          >
              <div
                className="relative isolate z-0 flex h-[5.1rem] min-w-0 max-w-full items-center justify-center overflow-visible"
                style={{
                  width: `min(28rem, calc(75% / ${isStockNavigatorsTenant || isJDubTradesTenant || isTravelingTraderTenant || isHerTenant || isChartGuysTenant || isPbAlertsTenant ? topNavLogoScale : topNavRenderedLogoScale}))`,
                }}
              >
              {/* Render only the combined horizontal asset. */}
              {whiteLabel?.useTextLogo ? (
                <span className={`max-w-full truncate px-2 text-center text-lg font-semibold tracking-wide ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {getTextLogoName(whiteLabel.companyName)}
                </span>
              ) : horizontalLogoSrc && (
                <img
                  src={horizontalLogoSrc}
                  alt={whiteLabel?.appName || brand.appName || 'Trade-Proof'}
                  className="relative z-[1] h-full w-full object-contain"
                  style={{ transform: `scale(${topNavRenderedLogoScale})` }}
                />
              )}
            </div>
          </div>

        {/* Left Navigation Group */}
        <div className="col-start-1 row-start-1 hidden min-w-0 items-center justify-start gap-[clamp(1.25rem,2.2vw,3rem)] min-[1440px]:flex">
          {leftItems.map((item) => (
            <button
               key={item.name}
               onClick={() => navigate(item.name)}
               data-tour-target={
                 item.name === 'Dashboard'
                   ? 'top-nav-dashboard'
                   : item.name === 'Calendar'
                   ? 'top-nav-calendar'
                   : item.name === 'Reports'
                   ? 'top-nav-trends'
                   : item.name === 'Trades'
                   ? 'top-nav-trades'
                   : item.name === 'Journal'
                   ? 'top-nav-journal'
                   : undefined
               }
               className={`${menuButtonBaseClass} ${activeTab === item.name ? menuButtonActiveClass : menuButtonInactiveClass}`}
             >
               {/* Icon wrapper with explicit active/hover color to match right-side highlight */}
               <div className="pr-1 relative z-10">
                 <item.icon className={`w-5 h-5 ${activeTab === item.name ? activeIconClass : menuIconInactiveClass}`} />
               </div>
               {/* Static Title below icon - match icon color and hover behavior */}
               <span className={`relative z-10 text-[10px] ${!isDark ? 'font-medium' : 'font-regular'} uppercase tracking-widest text-left ${activeTab === item.name ? activeTextClass : menuIconInactiveClass}`}>{item.name === 'Reports' ? 'Trends' : item.name}</span>
             </button>
          ))}

          {/* New Trade at the end */}
          <button onClick={() => navigate('New Trade')} className={`${menuButtonBaseClass} ${activeTab === 'New Trade' ? menuButtonActiveClass : menuButtonInactiveClass}`}>
            <div className="pr-1 relative z-10">
              <FileText className={`w-5 h-5 ${activeTab === 'New Trade' ? activeIconClass : menuIconInactiveClass}`} />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center border-2 border-slate-500">
                <Plus className="w-3 h-3 text-white" />
              </div>
            </div>
            <span className={`relative z-10 text-[10px] ${!isDark ? 'font-medium' : 'font-regular'} uppercase tracking-widest text-left`}>New Trade</span>
          </button>
        </div>

        {/* Far Right User Actions */}
        <div className="col-start-3 row-start-1 min-w-0 justify-self-end">
          <UserActions
            onNavigate={navigate}
            onLogoutStart={onLogoutStart}
            displayName={displayName}
            email={email}
            avatarUrl={avatarUrl}
            showAdminTools={showAdminTools}
            showBusiness={showBusiness}
            setNavLayout={setNavLayout}
            navLayout={navLayout}
            theme={theme}
            setTheme={setTheme}
            canSwitchTheme={canSwitchTheme}
            activePage={activeTab}
            layoutPresetsButtonRef={layoutPresetsButtonRef}
            tenantSlug={whiteLabel?.tenantSlug}
          />
        </div>
      </nav>
      {/* Subtle glow rising from the bottom across the whole menu */}
      <div className="absolute inset-0 pointer-events-none z-30">
        <div
          className="absolute inset-x-0 bottom-0 h-full opacity-85"
          style={{
            background: glowBackground,
            filter: 'blur(18px)'
          }}
        />
      </div>

      {/* Subtle bottom stroke (gradient) to give a soft divider on the menu bottom - placed beneath the logo */}
      <div className="absolute bottom-0 left-0 w-full h-px pointer-events-none -z-10">
        <div className={`w-full h-px bg-gradient-to-r ${strokeGradient} opacity-80`} />
      </div>
    </div>
  );
};

export default GlassNavbar;
